import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { FormControl, Select, MenuItem, TextField, InputAdornment } from "@mui/material";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/images/TicketImages/search.svg";
import useClickOutside from "../../../../hooks/useClickOutside";
import { useSelector, useDispatch } from "react-redux";
import { getAllKbCategories } from "../../../../store/features/getCategoriesToKnowledgeBase";

const statusOptions = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "pendingReview", label: "Pending Review" },
  { value: "archived", label: "Archived" },
];

const visibilityOptions = [
  { value: "public", label: "Public" },
  { value: "internal", label: "Internal" },
];

const menuProps = {
  PaperProps: {
    sx: {
      width: "154px",
      borderRadius: "8px",
      border: "1px solid #D5DCE1",
      background: "#FFF",
      boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
      mt: "8px",
      color: "#405261",
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        px: "15px",
        "&:hover": { color: "#061522", borderRadius: "8px", background: "#F9FAFA" },
        "&.Mui-selected": { color: "#061522", borderRadius: "8px", background: "#F9FAFA", backgroundColor: "#F9FAFA" },
      },
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

export default function ArticlesBulkBar({ count, onBulkChange, onBulkDelete, groupId }) {
  if (!count) return null;

  const dispatch = useDispatch();
  const { data: categoriesData } = useSelector((state) => state.getAllKbCategories);
  const categoryOptions = Array.isArray(categoriesData?.data)
    ? categoriesData.data.flatMap(cat => [cat, ...(cat.children || [])])
    : [];


  useEffect(() => {
    if (groupId) {
      dispatch(getAllKbCategories({ group_id: groupId }));
    }
  }, [dispatch, groupId]);

  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [visibility, setVisibility] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [showCatDropdown, setShowCatDropdown] = useState(false);

  const categoryDropdownRef = useRef(null);
  useClickOutside(categoryDropdownRef, () => setShowCatDropdown(false));

  const filteredCategories = categoryOptions.filter(cat =>
    (cat?.name || cat).toLowerCase().includes(categorySearch.toLowerCase())
  );

  const hasChanges = !!status || !!categoryId || !!visibility;

  const selectStyle = {
    borderRadius: "8px",
    borderColor: "#D5DCE1",
    background: "#FFF",
    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
    fontSize: "14px",
    height: "32px",
    color: "#405261",
    mr: 1,
    fontFamily: "Inter",
    textAlign: "center",
    display: 'flex',
    alignItems: 'center',
    '& .MuiSelect-select': {
      padding: '0px 16px',
      color: '#405261',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 400,
      textAlign: 'center',
      lineHeight: '24px',
      minHeight: 'unset',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-icon': { fontSize: 16, right: 8 },
  };

  const handleApply = () => {
    if (status) onBulkChange("status", status);
    if (categoryId) onBulkChange("category", categoryId);
    if (visibility) onBulkChange("visibility", visibility);
  };
  const handleReset = () => {
    setStatus("");
    setCategory("");
    setCategoryId("");
    setVisibility("");
    setCategorySearch("");
  };

  return (
    <Box sx={{
      mt: 2,
      mb: '8px',
      pl: "24px",
      pr: "24px",
      display: "flex",
      alignItems: "center",
      background: "transparent",
      justifyContent: 'space-between',
      width: '100%',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Status Select */}
        <FormControl size="small" sx={selectStyle}>
          <Select
            displayEmpty
            IconComponent={Dropdown}
            value={status}
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#405261" }}>Status</span>;
              }
              const option = statusOptions.find(o => o.value === selected);
              const label = option ? option.label : selected;
              return (
                <Box component="span" sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  width: '100%',
                  textAlign: 'center'
                }}>
                  {label}
                </Box>
              );
            }}
            onChange={e => setStatus(e.target.value)}
            sx={{ height: "32px", width: "104px" }}
            MenuProps={menuProps}
          >
            {statusOptions.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box ref={categoryDropdownRef} sx={{ position: 'relative', minWidth: 120 }}>
          <Box
            onClick={() => setShowCatDropdown(v => !v)}
            sx={{
              ...selectStyle,
              justifyContent: 'space-between',
              width: 120,
              px: '16px',
              border: '1px solid #D5DCE1',
              cursor: 'pointer',
              position: 'relative',
              height: 32,
              display: 'flex',
              alignItems: 'center',
              lineHeight: '24px',

              background: '#FFF'
            }}
          >
            <Box component="span" sx={{
              display: 'block',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: category ? '#061522' : '#405261',
              fontSize: 14,
              fontFamily: 'Inter',
              fontWeight: 400,
              textAlign: 'left'
            }}>
              {category || 'Category'}
            </Box>
            <Dropdown style={{ marginLeft: '6px', fontSize: 16, transition: 'transform 0.2s', transform: showCatDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </Box>
          {showCatDropdown && (
            <Box sx={{
              position: 'absolute',
              left: 0,
              top: '40px',
              zIndex: 1000,
              width: 280,
              height: 193,
              border: '1px solid #D5DCE1',
              borderRadius: '8px',
              background: '#FFF',
              boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
              p: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
              onClick={e => e.stopPropagation()}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search category"
                value={categorySearch}
                onChange={e => setCategorySearch(e.target.value)}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon style={{ width: 18, height: 18, color: '#CED6DF' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '8px',
                    background: '#FFF',
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    px: 1
                  }
                }}
                sx={{ mb: 1, mt: 0.5 }}
              />
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {filteredCategories.length > 0 ? filteredCategories.map((cat, idx) => {
                  const catName = cat?.name || cat;
                  const catId = cat?._id || cat?.id || catName;
                  return (
                    <MenuItem
                      key={catId}
                      value={catName}
                      selected={category === catName}
                      onMouseDown={() => {
                        setCategory(catName);
                        setCategoryId(cat?._id || cat?.id || "");
                        setShowCatDropdown(false);
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontFamily: 'Inter',
                        fontSize: 14,
                        borderRadius: '8px',
                        fontWeight: 400,
                        color: category === catName ? '#405261' : '#556575',
                        backgroundColor: '#FFF',
                        '&:hover': {
                          backgroundColor: '#F9FAFA',
                          color: '#405261',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#F9FAFA',
                          color: '#405261',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#F9FAFA',
                        },
                      }}
                    >
                      {catName}
                    </MenuItem>
                  );
                }) : (
                  <Box sx={{ color: '#93A1B0', px: 2, py: 1, textAlign: 'center', fontSize: 14 }}>No category found</Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Visibility Select */}
        <FormControl size="small" sx={selectStyle}>
          <Select
            displayEmpty
            IconComponent={Dropdown}
            value={visibility}
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#405261" }}>Visibility</span>;
              }
              const option = visibilityOptions.find(o => o.value === selected);
              const label = option ? option.label : selected;
              return (
                <Box component="span" sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  width: '100%',
                  textAlign: 'center'
                }}>
                  {label}
                </Box>
              );
            }}
            onChange={e => setVisibility(e.target.value)}
            sx={{ height: "32px", width: "120px" }}
            MenuProps={menuProps}
          >
            {visibilityOptions.map((v) => (
              <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Delete Button */}
        <Button
          size="small"
          onClick={onBulkDelete}
          sx={{
            width: "88px",
            height: "32px",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            background: "#FFF",
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            color: "#FF4D4F",
            textAlign: "center",
            fontFamily: "Inter",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "24px",
            textTransform: "none",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0
          }}
        >
          Delete
        </Button>
      </Box>

      {/* RIGHT group: only Reset/Apply */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          sx={{
            width: "72px",
            height: "32px",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            background: "#FFF",
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            color: "#405261",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "18px",
            textTransform: "none",
            "&:not(.Mui-disabled):hover": {
              backgroundColor: "#FFFFFF",

              borderColor: "#C5CED6",
            },
            "&.Mui-disabled": {
              color: "#93A1B0",
              border: "1px solid #C5CED6",
              bgcolor: "#F9FAFA",
            },
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!hasChanges}
          sx={{
            height: "32px",
            borderRadius: "8px",
            width: "72px",
            background: "#148DFD",
            textTransform: "none",
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            fontSize: '14px',
            fontWeight: 500,
            "&:not(.Mui-disabled):hover": {
              backgroundColor: "#0077CC",

            },
            "&.Mui-disabled": {

              color: "#93A1B0",
              border: "1px solid #C5CED6",
              bgcolor: "#F9FAFA",
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
}